import React, { PureComponent } from 'react';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { withTranslation } from 'react-i18next';
import Cookies from 'js-cookie';
import { debounce } from 'underscore';
import queryString from 'query-string';

import { setChatEmail, setChatUserId, setChatUsername, setShowLoginForm } from '../../actions/chatLoginActions';
import { setShowBackgroundElements, setReactionId, setPollsData, setQnAData, setQuestionsData } from '../../actions/playerActions';
import {
	setViewerEngagementModuleActivated,
	setPollExpanded,
	setReactionUrl,
	setChatActivated,
	setMediasData,
	setCloseIncomingMessage,
} from '../../actions/configurationActions';
import { chatPublicDomain } from '../../util/config';
import { generateUUID, isUsedOnMobile, joinClassNames } from '../../util/util';

import Poll from '../Poll/Poll';
import ChatUpdated from '../ChatUpdated/ChatUpdated';
import QuestionPanel from '../Q&A/QuestionPanel';

import ChatLoginOverlay from './ChatLoginOverlay';
import { ChatPinnedMessage } from './PinnedMessage.styled';
import { ChatBottom } from './ChatBottom.styled';
import { ChatTop } from './ChatTop.styled';
import { ChatOverlayContainer, ChatOverlayWrapper, ChatWrapper } from './Chat.styled';
import { ChatButtonsWrapper } from './ChatButtons.styled';
import { NOTIFICATION_MESSAGES } from './helpers';
import { NotificationUtils } from './NotificationUtils';

import './ChatOverlay.scss';

class ChatOverlay extends PureComponent {
	constructor() {
		super();
		this.state = {
			showPinnedMessage: false,
			inputChatString: '',
			showPicker: false,
			messagesArray: [],
			pinnedMessagesArray: [],
			getChatUrl: '',
			getReactionUrl: '',
			chatVisible: true,
			basketItemCount: 0,
			pollArray: [],
			publishedPoll: undefined,
			pollResult: undefined,
			disabledChatInput: false,
			publishedQnA: undefined,
			approvedQuestions: [],
			allQuestions: [],
		};

		this.messageContainerRef = React.createRef(null);
		this.justSendMessage = false;
		this.justSendQuestion = false;
		this.webSocketConnection = null;
		this.chatWrapperBottom = 0;
	}

	componentDidMount() {
		this.connectToTheSocket();
		this.enableBasketObserver();
		this.enablePreBasketObserver();
		this.enableItemsCountersObserver();
		this.scrollToBottom();
		this.getChatUserInfoFromCookie();

		const { viewerEngagementModuleActivated, settings, isPlaying } = this.props;
		const shouldRefetchChatData =
			viewerEngagementModuleActivated && settings?.engagementFeatures?.enabled && settings?.engagementFeatures?.showChat;
		if (shouldRefetchChatData && isPlaying !== undefined) {
			this.fetchChatData();

			const bottomControlbar = document.getElementsByClassName('bottomControlbar')[0];
			if (bottomControlbar) {
				this.chatWrapperBottom = bottomControlbar.getBoundingClientRect().height;
			}
		}
	}

	componentDidUpdate(prevProps, prevState) {
		if (this.props.showChatWindow !== prevProps.showChatWindow && this.props.showChatWindow) {
			setTimeout(this.scrollToBottom, 1000);
		}
		if (this.state.messagesArray?.length !== prevState.messagesArray?.length) {
			this.checkAndScrollToBottom();
		}

		const { viewerEngagementModuleActivated, isChatActivated, settings, mediaId, isPlaying } = this.props;
		const checkedViewerEngagementModuleActivated =
			viewerEngagementModuleActivated && viewerEngagementModuleActivated !== prevProps.viewerEngagementModuleActivated;
		const checkIsChatActivated = isChatActivated && isChatActivated !== prevProps.isChatActivated;

		if (
			mediaId !== prevProps.mediaId ||
			checkedViewerEngagementModuleActivated ||
			checkIsChatActivated ||
			(isPlaying !== undefined && prevProps.isPlaying === undefined)
		) {
			const shouldRefetchChatData =
				viewerEngagementModuleActivated &&
				isPlaying !== undefined &&
				settings?.engagementFeatures?.enabled &&
				settings?.engagementFeatures?.showChat;

			if (shouldRefetchChatData) {
				this.connectToTheSocket();
				this.fetchChatData();
			}

			if (isPlaying !== undefined && prevProps.isPlaying === undefined) {
				const bottomControlbar = document.getElementsByClassName('bottomControlbar')[0];
				this.chatWrapperBottom = bottomControlbar ? bottomControlbar.getBoundingClientRect().height : 60;
			}
		}

		if (this.props.playerReady !== prevProps.playerReady && this.props.playerReady) {
			this.connectToTheSocket();
			this.enableBasketObserver();
			this.enablePreBasketObserver();
			this.enableItemsCountersObserver();
		}
	}

	isIgnoreChatCookies = () => {
		const queryParams = queryString.parse(window.location.search);
		if (queryParams.ignoreChatCookies) {
			return queryParams.ignoreChatCookies === 'true';
		}

		return this.props.settings?.additionalSettings?.noChatCookies === true;
	};

	sendQuestion = debounce((event, questionBody) => {
		const { accountId, mediaId } = this.props;
		const { publishedQnA } = this.state;
		this.justSendQuestion = true;

		if (!publishedQnA) {
			return;
		}

		event.preventDefault?.();
		const questionUrl = `${chatPublicDomain}${accountId}/medias/${mediaId}/qnas/${publishedQnA.id}/questions`;
		fetch(questionUrl, {
			method: 'POST',
			headers: {
				Accept: 'application/json',
				'Content-Type': 'application/json',
			},
			body: JSON.stringify(questionBody),
		});

		if (window.mobileCheck && window.mobileCheck()) {
			document.activeElement.blur();
		}
	}, 2000);

	upvoteQuestion = debounce((event, questionId, qnaId) => {
		const { accountId, mediaId } = this.props;
		this.justSendQuestion = true;

		event.preventDefault?.();
		const upvoteQuestionUrl = `${chatPublicDomain}${accountId}/medias/${mediaId}/qnas/${qnaId}/questions/${questionId}/upvote`;
		fetch(upvoteQuestionUrl, {
			method: 'POST',
			headers: {
				Accept: 'application/json',
				'Content-Type': 'application/json',
			},
		});

		if (!this.isIgnoreChatCookies()) {
			const upvotedQuestions = Cookies.get(`chat_upvoted_${accountId}`);
			const newUpvotedQuestion = `${upvotedQuestions ?? ''},${questionId}`;
			Cookies.set(`chat_upvoted_${accountId}`, newUpvotedQuestion);
		}

		if (window.mobileCheck && window.mobileCheck()) {
			document.activeElement.blur();
		}
	}, 2000);

	sendMessage = debounce((event, messageBody) => {
		this.justSendMessage = true;
		event.preventDefault?.();

		fetch(this.state.getChatUrl, {
			method: 'POST',
			headers: {
				Accept: 'application/json',
				'Content-Type': 'application/json',
			},
			body: JSON.stringify(messageBody),
		});

		this.setState({
			disabledChatInput: false,
		});

		if (window.mobileCheck && window.mobileCheck()) {
			document.activeElement.blur();
		}
	}, 2000);

	sendReaction = debounce(() => {
		var tempObj = {
			sender: {
				userId: this.props.userId !== '' ? this.props.userId : 'notLoggedId',
				name: this.props.username !== '' ? this.props.username : 'notLoggedName',
				email: this.props.email !== '' ? this.props.email : 'notLoggedEmail',
				admin: false,
			},
			message: {
				emoji: {
					unicode: '\u2764\uFE0F',
					level: 1,
				},
			},
		};

		const currentMedia = this.props.mediasData.find(({ id, originalId }) => id === this.props.mediaId || originalId === this.props.mediaId);
		window.parent.postMessage(
			{
				origin: 'Qbrick.Interact.Widget.Element',
				identifier: 'qbrick-player-analytics-message',
				command: {
					type: 'AnalyticsEvent',
					action: 'click',
					data: 'reaction',
					mediaName: this.props.mediaName,
					mediaId: this.props.mediaId,
					accountId: this.props.accountId,
					duration: currentMedia?.duration ?? this.props.duration,
					isMediaLive: this.props.isMediaLive,
					currentPosition: this.props.isMediaLive ? Date.now() : this.props.currentPosition,
				},
			},
			'*'
		);

		fetch(this.props.reactionUrl, {
			method: 'POST',
			headers: {
				Accept: 'application/json',
				'Content-Type': 'application/json',
			},
			body: JSON.stringify(tempObj),
		});
	}, 2000);

	getChatUserInfoFromCookie = () => {
		const { accountId, setChatUsername, setChatUserId, setChatEmail } = this.props;
		const userId = Cookies.get(`chat_ID_${accountId}`);
		const userName = Cookies.get(`chat_name_${accountId}`);
		const userEmail = Cookies.get(`chat_email_${accountId}`);

		if (this.isIgnoreChatCookies()) {
			Cookies.remove(`chat_ID_${accountId}`);
			Cookies.remove(`chat_name_${accountId}`);
			Cookies.remove(`chat_email_${accountId}`);
			Cookies.remove(`chat_upvoted_${accountId}`);
			setChatUserId('');
			setChatUsername('');
			setChatEmail('');
		} else {
			userId && setChatUserId(userId);
			userName && setChatUsername(userName);
			userEmail && setChatEmail(userEmail);
		}
	};

	checkAndScrollToBottom = () => {
		const currentMessageContainer = this.messageContainerRef.current;
		if (!currentMessageContainer) {
			return;
		}

		if (
			this.justSendMessage ||
			Math.abs(currentMessageContainer.scrollHeight - currentMessageContainer.scrollTop - currentMessageContainer.clientHeight) <= 100
		) {
			this.scrollToBottom();
		}
	};

	scrollToBottom = () => {
		if (!this.messageContainerRef.current) {
			return;
		}

		const allMessages = this.messageContainerRef.current.querySelectorAll('.chat-message');
		const lastMessage = allMessages[allMessages.length - 1];
		this.justSendMessage = false;
		if (lastMessage) {
			lastMessage.scrollIntoView({
				behavior: 'smooth',
				block: 'nearest',
			});
		}
	};

	enableBasketObserver = () => {
		var target = document.querySelector('.gobrain-shopping-basket');
		if (!target) {
			return;
		}

		var observer = new MutationObserver((mutations) => {
			mutations.forEach((mutation) => {
				if (mutation.type === 'attributes') {
					if (target.getAttribute('style') === 'display: block;') {
						this.setStatusOfChatVisibility(false);
						this.props.setShowBackgroundElements(false);
					} else if (target.getAttribute('style') === 'display: none;') {
						this.setStatusOfChatVisibility(true);
						this.props.setShowBackgroundElements(true);
					}
				}
			});
		});

		var config = {
			attributes: true,
		};

		observer.observe(target, config);
	};

	enablePreBasketObserver = () => {
		var target = document.querySelector('.gobrain-shopping-pre-basket');
		if (!target) {
			return;
		}

		var observer = new MutationObserver((mutations) => {
			mutations.forEach((mutation) => {
				if (mutation.type === 'attributes') {
					if (target.getAttribute('style') === 'display: block;') {
						this.setStatusOfChatVisibility(false);
						this.props.setShowBackgroundElements(false);
					} else if (target.getAttribute('style') === 'display: none;') {
						this.setStatusOfChatVisibility(true);
						this.props.setShowBackgroundElements(true);
					}
				}
			});
		});

		var config = {
			attributes: true,
		};

		observer.observe(target, config);
	};

	enableItemsCountersObserver = () => {
		var target = document.querySelector('.gobrain-show-basket-item-count');
		if (!target) {
			return;
		}

		var observer = new MutationObserver((mutations) => {
			mutations.forEach(() => {
				this.setState({
					basketItemCount: document.querySelector('.gobrain-show-basket-item-count')?.firstChild?.textContent,
				});
			});
		});

		var config = {
			attributes: true,
			characterData: true,
			childList: true,
			subtree: true,
			attributeOldValue: true,
			characterDataOldValue: true,
		};

		observer.observe(target, config);
	};

	setStatusOfChatVisibility = (status) => {
		this.setState({
			chatVisible: status,
		});
	};

	updatePollState = (polls) => {
		if (polls && polls.length > 0 && polls.find) {
			const lastPublishedPoll = polls.find((p) => p.published);
			if (lastPublishedPoll) {
				this.setState({
					pollArray: polls,
					publishedPoll: lastPublishedPoll,
					pollResult: lastPublishedPoll.closed ? lastPublishedPoll.result : undefined,
				});
			} else {
				this.setState({ pollArray: polls, publishedPoll: undefined, pollResult: undefined });
			}
		} else {
			this.setState({ pollArray: [], publishedPoll: undefined, pollResult: undefined });
		}
	};

	updateQnAState = (qnas, questions) => {
		const lastPublishedQna = qnas.find((qna) => qna.published);
		const approvedQuestions = (questions ?? []).filter((q) => q.approved && !q.discarded);
		this.setState({
			publishedQnA: lastPublishedQna,
			approvedQuestions: approvedQuestions,
			allQuestions: questions,
		});
	};

	fetchChatData = () => {
		this.setState({ getChatUrl: chatPublicDomain + this.props.accountId + '/medias/' + this.props.mediaId + '/messages' }, () => {
			this.fetchChatMessages();
			this.fetchPollData();
			this.fetchQnAData();
		});

		this.props.setReactionUrl(chatPublicDomain + this.props.accountId + '/medias/' + this.props.mediaId + '/reactions');
	};

	fetchChatMessages = () => {
		if (!this.props.isChatActivated) {
			return;
		}
		fetch(this.state.getChatUrl)
			.then((res) => res.json())
			.then((data) => {
				var tempChatMessages = [];
				var tempPinnedMessagesArray = [];

				data.map(function (item) {
					if (item.message?.pinned) {
						tempPinnedMessagesArray = [];
						tempPinnedMessagesArray.push(item);
					}

					tempChatMessages.push(item);
				});

				this.setState({
					messagesArray: tempChatMessages,
					pinnedMessagesArray: tempPinnedMessagesArray,
				});
			});
	};

	fetchPollData = () => {
		const { accountId, mediaId, setPollsData, getRestrictToken } = this.props;
		const pollUrl = `${chatPublicDomain}${accountId}/medias/${mediaId}/polls`;
		fetch(
			pollUrl,
			getRestrictToken(mediaId)
				? {
						headers: {
							'Content-Type': 'application/json',
							'X-Authorization': 'AVP1 ' + getRestrictToken(mediaId),
						},
					}
				: undefined
		)
			.then((res) => res.json())
			.then((data) => {
				setPollsData(data);
				this.updatePollState(data);
			});
	};

	fetchQuestions = (qnas) => {
		const { accountId, mediaId, setQuestionsData, getRestrictToken } = this.props;
		const questionUrl = `${chatPublicDomain}${accountId}/medias/${mediaId}/qnas/${qnas[0].id}/questions`;
		fetch(
			questionUrl,
			getRestrictToken(mediaId)
				? {
						headers: {
							'Content-Type': 'application/json',
							'X-Authorization': 'AVP1 ' + getRestrictToken(mediaId),
						},
					}
				: undefined
		)
			.then((res) => res.json())
			.then((questions) => {
				setQuestionsData(questions ?? []);
				this.updateQnAState(qnas, questions);
			});
	};

	fetchQnAData = () => {
		const { accountId, mediaId, setQnAData, getRestrictToken } = this.props;
		const qnaUrl = `${chatPublicDomain}${accountId}/medias/${mediaId}/qnas`;
		fetch(
			qnaUrl,
			getRestrictToken(mediaId)
				? {
						headers: {
							'Content-Type': 'application/json',
							'X-Authorization': 'AVP1 ' + getRestrictToken(mediaId),
						},
					}
				: undefined
		)
			.then((res) => res.json())
			.then((qnas) => {
				setQnAData(qnas ?? []);
				if (qnas?.[0]?.id) {
					this.fetchQuestions(qnas);
				}
			});
	};

	onSocketOpen = (webSocketConnection) => {
		if (NotificationUtils.isSocketConnectionReady(webSocketConnection)) {
			this.scrollToBottom();
		}
	};

	connectToTheSocket = () => {
		const { mediaId, accountId, playlistId } = this.props;

		if (this.webSocketConnection && this.webSocketConnection.close) {
			this.webSocketConnection.close();
		}

		const playerElement = document.getElementById(`player-${playlistId || mediaId}`);
		if (!playerElement) {
			return;
		}

		this.webSocketConnection = NotificationUtils.connectToSocketServer({
			onOpen: this.onSocketOpen,
			onMessage: (res) => this.parseMessages(res),
			mediaId,
			accountId,
		});
	};

	parseMessages = (response) => {
		this.parseLiveCustomMessage(response);
		if (!this.props.isCloseIncomingMessage) {
			this.parseChatMessages(response);
			this.parsePollMessages(response);
			this.parseQnAMessages(response);
		}
	};

	parseLiveCustomMessage = (response) => {
		const responseMessage = JSON.parse(response.data);

		const {
			isChatActivated,
			setChatActivated,
			viewerEngagementModuleActivated,
			setViewerEngagementModuleActivated,
			mediaId,
			mediasData,
			setMediasData,
			setCloseIncomingMessage,
			isCloseIncomingMessage,
		} = this.props;

		if (responseMessage?.key === NOTIFICATION_MESSAGES.customPropUpdated && responseMessage.body?.mediaId === mediaId) {
			const customProperty = JSON.parse(responseMessage.body?.customProperty?.value ?? '');
			const shouldActivateViewerEngagementModule = customProperty?.activatedViewerEngagement && !customProperty?.hideEngagementModule;
			const shouldActivateChat = customProperty?.activatedChat !== false;

			const currentMedia = mediasData.find((media) => media?.id === mediaId || media?.originalId === mediaId);
			if (currentMedia) {
				currentMedia.custom = customProperty;
				const newMediasData = [...mediasData].map((media) => (media.id === mediaId ? currentMedia : media));
				setMediasData(newMediasData);
			}

			if (viewerEngagementModuleActivated !== shouldActivateViewerEngagementModule) {
				setViewerEngagementModuleActivated(shouldActivateViewerEngagementModule);
			}
			if (shouldActivateChat !== isChatActivated) {
				setChatActivated(shouldActivateChat);
			}
			if (!!customProperty?.closeIncomingMessage !== isCloseIncomingMessage) {
				setCloseIncomingMessage(!!customProperty?.closeIncomingMessage);
			}
		}
	};

	parseQnAMessages = (response) => {
		const responseMessage = JSON.parse(response.data);
		const messageKey = responseMessage.key;
		const responseMessageBody = responseMessage.body ?? {};

		switch (messageKey) {
			case NOTIFICATION_MESSAGES.qnaQuestionCreated:
				this.setState((prevState) => ({
					allQuestions: [...prevState.allQuestions, { ...responseMessageBody, created: responseMessageBody.timestamp }],
				}));
				break;
			case NOTIFICATION_MESSAGES.qnaPublished:
				this.setState({ publishedQnA: responseMessageBody });
				break;
			case NOTIFICATION_MESSAGES.qnaUnpublished:
				this.setState((prevState) => {
					if (!prevState.publishedQnA || prevState.publishedQnA.id !== responseMessageBody.id) {
						return null;
					}
					return {
						...prevState,
						publishedQnA: undefined,
					};
				});
				break;
			case NOTIFICATION_MESSAGES.qnaUpdated:
				this.setState((prevState) => {
					if (!prevState.publishedQnA || prevState.publishedQnA.id !== responseMessageBody.id) {
						return null;
					}

					return { ...prevState, publishedQnA: responseMessageBody };
				});
				break;
			case NOTIFICATION_MESSAGES.qnaQuestionDiscardChanged:
				this.setState((prevState) => {
					const filteredAllQuestions = prevState.allQuestions.filter(
						(q) => q.id !== responseMessageBody.id && q.recipient?.replyQuestionId !== responseMessageBody.id
					);
					const updatedQuestion = prevState.allQuestions.find((q) => q.id === responseMessageBody.id);
					const updatedReply = prevState.allQuestions.find((q) => q.recipient?.replyQuestionId === responseMessageBody.id);
					if (!updatedQuestion) {
						return null;
					}
					updatedQuestion.discarded = responseMessageBody.discarded;

					// In case the question doesn't have replied yet.
					if (updatedReply) {
						updatedReply.discarded = responseMessageBody.discarded;
					}
					const newAllQuestions = [...filteredAllQuestions, updatedQuestion, updatedReply].filter(Boolean);
					const newApprovedQuestions = newAllQuestions.filter((q) => q.approved && !q.discarded);

					return { ...prevState, approvedQuestions: newApprovedQuestions, allQuestions: newAllQuestions };
				});
				break;
			case NOTIFICATION_MESSAGES.qnaQuestionApproveChanged:
				this.setState((prevState) => {
					const filteredAllQuestions = prevState.allQuestions.filter((q) => q.id !== responseMessageBody.id);
					const updatedQuestion = prevState.allQuestions.find((q) => q.id === responseMessageBody.id);
					if (!updatedQuestion) {
						return null;
					}
					updatedQuestion.approved = responseMessageBody.approved;
					// update created value to make sure new question will be on bottom of messages
					updatedQuestion.created = new Date().toISOString();

					const newAllQuestions = [...filteredAllQuestions, updatedQuestion].filter(Boolean);
					const newApprovedQuestions = newAllQuestions.filter((q) => q.approved && !q.discarded);

					return { ...prevState, approvedQuestions: newApprovedQuestions, allQuestions: newAllQuestions };
				});
				break;
			case NOTIFICATION_MESSAGES.qnaQuestionAnswered:
				this.setState((prevState) => {
					const filteredAllQuestions = prevState.allQuestions.filter((q) => q.id !== responseMessageBody.id);
					const originalQuestion = prevState.allQuestions.find((q) => q.id === responseMessageBody.id);

					if (!originalQuestion) {
						return null;
					}

					// BE now return 2 records for question and answer, therefore doing the same for ui
					const answeredQuestion = {
						...originalQuestion,
						recipient: responseMessageBody.recipient,
						reply: responseMessageBody.reply,
						sender: responseMessageBody.sender,
						created: new Date().toISOString(),
					};

					const newAllQuestions = [...filteredAllQuestions, originalQuestion, answeredQuestion].filter(Boolean);
					const newApprovedQuestions = newAllQuestions.filter((q) => q.approved && !q.discarded);

					return {
						approvedQuestions: newApprovedQuestions,
						allQuestions: newAllQuestions,
					};
				});
				break;
			case NOTIFICATION_MESSAGES.qnaQuestionUpvoted:
				this.setState((prevState) => {
					const filteredAllQuestions = prevState.allQuestions.filter((q) => q.id !== responseMessageBody.id);
					const updatedReply = prevState.allQuestions.find((q) => q.id === responseMessageBody.id && q.reply);
					const updatedQuestion = prevState.allQuestions.find((q) => q.id === responseMessageBody.id && !!q.question);
					if (!updatedQuestion) {
						return null;
					}
					updatedQuestion.votes = responseMessageBody.votes;

					const newAllQuestions = [...filteredAllQuestions, updatedReply, updatedQuestion].filter(Boolean);
					const newApprovedQuestions = newAllQuestions.filter((q) => q.approved && !q.discarded);
					return {
						...prevState,
						approvedQuestions: newApprovedQuestions,
						allQuestions: newAllQuestions,
					};
				});
				break;
		}
	};

	parseChatMessages = (response) => {
		const responseMessage = JSON.parse(response.data);
		const messageKey = responseMessage.key;
		const responseMessageBody = responseMessage.body ?? {};

		switch (messageKey) {
			case NOTIFICATION_MESSAGES.messageCreated:
				this.setState((prevState) => {
					const filteredMessages = prevState.messagesArray.filter((m) => m.id !== responseMessageBody.id);
					const newMessage = { ...responseMessageBody, created: responseMessageBody.created ?? responseMessageBody.timestamp };
					return {
						...prevState,
						messagesArray: [...filteredMessages, newMessage],
						pinnedMessagesArray: responseMessageBody.message?.pinned ? [newMessage] : prevState.pinnedMessagesArray,
					};
				});
				break;

			case NOTIFICATION_MESSAGES.messagePinned:
				this.setState((prevState) => {
					const newMessageArray = (prevState.messagesArray ?? []).map((messBody) => ({
						...messBody,
						message: {
							...messBody.message,
							pinned: messBody.id === responseMessageBody.id,
						},
					}));
					const currentMess = newMessageArray.find((messBody) => messBody.id === responseMessageBody.id);

					return {
						...prevState,
						messagesArray: newMessageArray,
						pinnedMessagesArray: [currentMess],
					};
				});
				break;

			case NOTIFICATION_MESSAGES.messageUnPinned:
				this.setState((prevState) => {
					const newMessageArray = (prevState.messagesArray ?? []).map((messBody) => ({
						...messBody,
						message: {
							...messBody.message,
							pinned: messBody.id === responseMessageBody.id ? false : messBody.message.pinned,
						},
					}));

					return {
						...prevState,
						messagesArray: newMessageArray,
						pinnedMessagesArray: [],
					};
				});
				break;

			case NOTIFICATION_MESSAGES.messageBlocked:
				this.setState((prevState) => {
					const newMessageArray = (prevState.messagesArray ?? [])
						.map((messBody) => {
							if (responseMessageBody.messageIds.includes(messBody.id)) {
								return undefined;
							}
							return messBody;
						})
						.filter(Boolean);
					const currentPinnedMess = (prevState.pinnedMessagesArray ?? []).find((messBody) =>
						responseMessageBody.messageIds.includes(messBody.id)
					);
					const newPinnedMessageArray = currentPinnedMess ? [] : prevState.pinnedMessagesArray;

					return {
						...prevState,
						messagesArray: newMessageArray,
						pinnedMessagesArray: newPinnedMessageArray,
					};
				});
				break;

			case NOTIFICATION_MESSAGES.reactionCreated:
				var timestamp = new Date().getUTCMilliseconds();
				this.props.setReactionId(timestamp);
				break;

			default:
				return;
		}
	};

	parsePollMessages = (response) => {
		const responseMessage = JSON.parse(response.data);
		const messageKey = responseMessage.key;
		const responseMessageBody = responseMessage.body ?? {};

		switch (messageKey) {
			case NOTIFICATION_MESSAGES.pollCreated:
				this.setState((prevState) => ({
					...prevState,
					pollArray: [...prevState.pollArray, responseMessageBody],
				}));
				break;

			case NOTIFICATION_MESSAGES.pollUpdated:
				this.setState((prevState) => {
					const newPolls = prevState.pollArray.map((prevPoll) => (prevPoll.id === responseMessageBody.id ? responseMessageBody : prevPoll));
					if (!prevState.pollArray.find((p) => p.id === responseMessageBody.id)) {
						newPolls.push(responseMessageBody);
					}

					return { ...prevState, pollArray: newPolls };
				});
				break;

			case NOTIFICATION_MESSAGES.pollPublished:
				this.setState(
					{
						publishedPoll: responseMessageBody,
						pollResult: responseMessageBody.result,
					},
					() => setTimeout(this.props.setPollExpanded(true))
				);
				break;

			case NOTIFICATION_MESSAGES.pollUnpublished:
				this.setState(
					(prevState) => {
						if (!prevState.publishedPoll || prevState.publishedPoll.id !== responseMessageBody.id) {
							return null;
						}
						return {
							...prevState,
							publishedPoll: undefined,
							pollResult: undefined,
						};
					},
					() => setTimeout(this.props.setPollExpanded(false))
				);
				break;

			case NOTIFICATION_MESSAGES.pollClosed:
				this.setState({
					publishedPoll: { ...responseMessageBody, closed: true },
					pollResult: responseMessageBody.result,
				});
				break;
			default:
				return;
		}
	};

	singleMessage = (id, user, text) => {
		return (
			<div className="singleMessageWraper" id={id}>
				<span className="singleMessageUser">{user}</span>
				<span className="singleMessageText">{text}</span>
			</div>
		);
	};

	handleFocusOnChatInput = () => {
		if (this.props.username === '') {
			this.props.setShowLoginForm(true);
			document.getElementById('chatInputFieldId')?.blur();
		}
	};

	handleEditProfileClick = () => {
		this.props.setShowLoginForm(this.props.username === '' ? true : 'edit');
	};

	onUpvoteButtonClick = (event, questionId, qnaId) => {
		this.upvoteQuestion(event, questionId, qnaId);
	};

	handleSendQuestion = (event, question) => {
		const { userId, username, email } = this.props;
		if (question?.text) {
			const questionBody = {
				id: generateUUID(),
				question: question.text,
				sender: {
					userId: userId,
					name: username,
					email: email,
					admin: false,
				},
			};
			this.sendQuestion(event, questionBody);
		}
	};

	handleMessagePost = (event, message, setMessageText) => {
		if (event.keyCode === 13 && message !== '') {
			const messageBody = {
				id: generateUUID(),
				sender: {
					userId: this.props.userId,
					name: this.props.username,
					email: this.props.email,
					admin: false,
				},
				message: {
					pinned: false,
					text: message,
				},
			};

			this.setState((prevState) => ({
				...prevState,
				inputChatString: '',
				disabledChatInput: true,
				messagesArray: [...prevState.messagesArray, { ...messageBody, created: new Date().toISOString() }],
			}));
			setMessageText && setMessageText('');

			this.sendMessage(event, messageBody);
		}
	};

	onEmojiClick = (_event, emojiObject) => {
		this.setState({
			inputChatString: this.state.inputChatString + emojiObject.emoji,
			showPicker: false,
		});
	};

	handleReactionPost = () => {
		this.sendReaction();
	};

	handleRefreshButtonClick = () => {
		this.fetchPollData();
		this.fetchQnAData();
		this.fetchChatMessages();
	};

	render() {
		const {
			showShoppingCart,
			showChatWindow,
			settings,
			isPollExpanded,
			showMobileChat,
			show,
			playerHover,
			reactionId,
			onTouchStart,
			onTouchMove,
			viewerEngagementModuleActivated,
			showCountdownModule,
			isPlaying,
			isChatActivated,
			isCloseIncomingMessage,
		} = this.props;

		const {
			publishedQnA,
			publishedPoll,
			pollResult,
			messagesArray,
			approvedQuestions,
			disabledChatInput,
			chatVisible,
			inputChatString,
			pinnedMessagesArray,
			showPicker,
			basketItemCount,
		} = this.state;

		const queryParams = queryString.parse(window.location.search);
		const isShowViewerEngagementModule =
			viewerEngagementModuleActivated && settings?.engagementFeatures?.enabled && settings?.engagementFeatures?.showChat;

		if (showShoppingCart || isPlaying === undefined || !isShowViewerEngagementModule || showCountdownModule) {
			return null;
		}

		if (isUsedOnMobile(queryParams)) {
			return (
				<>
					<ChatOverlayContainer
						onTouchStart={onTouchStart}
						onTouchMove={onTouchMove}
						hideRightControls={false}
						className={joinClassNames([
							'chat-overlay-mobile',
							playerHover || settings?.bottomControls?.alwaysVisible ? 'chat-overlay-mobile--hover' : '',
						])}
					>
						<ChatOverlayWrapper
							isMobile
							chatVisible={chatVisible}
							showMobileChat={showMobileChat}
							style={playerHover || settings?.bottomControls?.alwaysVisible ? { bottom: this.chatWrapperBottom + 'px' } : undefined}
						>
							<ChatWrapper showMobileChat={showMobileChat} className="chat-wrapper">
								{isShowViewerEngagementModule && (
									<>
										{isChatActivated && (
											<ChatBottom
												isMobile
												onEditProfileClick={this.handleEditProfileClick}
												inputChatString={inputChatString}
												onChange={(e) => {
													const value = e.target.value;
													if (value.trim().length > 300) {
														return;
													}
													this.setState({
														inputChatString: value,
													});
												}}
												handleMessagePost={this.handleMessagePost}
												handleFocusOnChatInput={this.handleFocusOnChatInput}
												emojiIconOnClick={() =>
													this.setState((prevState) => ({
														showPicker: !prevState.showPicker,
													}))
												}
												emojiOnClick={(e, emojiObject) => this.onEmojiClick(e, emojiObject)}
												showPicker={showPicker}
												disabledChatInput={disabledChatInput || isCloseIncomingMessage}
											/>
										)}
										{isChatActivated && (
											<ChatPinnedMessage
												settings={settings}
												pinnedMessagesArray={pinnedMessagesArray}
												messagesArray={messagesArray}
											/>
										)}
										{!isCloseIncomingMessage && (
											<Poll settings={settings} poll={publishedPoll} pollResult={pollResult} isMobile />
										)}
										{!isCloseIncomingMessage && (
											<QuestionPanel
												t={this.props.t}
												qna={publishedQnA}
												onSendQuestionClick={this.handleSendQuestion}
												onFocusOnQuestionField={this.handleFocusOnChatInput}
												settings={settings}
											/>
										)}
										<ChatTop
											settings={settings}
											isPollExpanded={isPollExpanded}
											messageContainerRef={this.messageContainerRef}
											messages={isChatActivated ? messagesArray : []}
											questions={approvedQuestions}
											onRefreshButtonClick={this.handleRefreshButtonClick}
											onUpvoteButtonClick={this.onUpvoteButtonClick}
											closeIncomingMessage={isCloseIncomingMessage}
										/>
									</>
								)}
							</ChatWrapper>
							<ChatButtonsWrapper
								isShowViewerEngagementModule={isShowViewerEngagementModule}
								reactionId={reactionId}
								settings={settings}
								basketItemCount={basketItemCount}
								handleReactionPost={this.handleReactionPost}
							/>
						</ChatOverlayWrapper>
					</ChatOverlayContainer>
					{isShowViewerEngagementModule && <ChatLoginOverlay settings={settings} isMobile />}
				</>
			);
		}

		return showChatWindow ? (
			<ChatUpdated
				onChatNotificationClick={this.scrollToBottom}
				settings={settings}
				poll={isCloseIncomingMessage ? undefined : publishedPoll}
				qna={isCloseIncomingMessage ? undefined : publishedQnA}
				pollResult={isCloseIncomingMessage ? undefined : pollResult}
				messages={messagesArray}
				questions={approvedQuestions}
				showChatWindow={showChatWindow}
				onKeyUp={this.handleMessagePost}
				onSendQuestionClick={this.handleSendQuestion}
				onFocusOnQuestionField={this.handleFocusOnChatInput}
				show={show}
				messageContainerRef={this.messageContainerRef}
				onChatInputFocus={this.handleFocusOnChatInput}
				onEditProfileClick={this.handleEditProfileClick}
				disabledChatInput={disabledChatInput}
				onRefreshButtonClick={this.handleRefreshButtonClick}
				onUpvoteButtonClick={this.onUpvoteButtonClick}
				closeIncomingMessage={isCloseIncomingMessage}
			/>
		) : null;
	}
}

const mapStateToProps = ({ chatLoginReducer, configurationReducer, playerReducer }) => ({
	username: chatLoginReducer.username,
	email: chatLoginReducer.email,
	userId: chatLoginReducer.userId,
	show: chatLoginReducer.show,
	showMobileChat: chatLoginReducer.showMobileChat,
	mediaName: configurationReducer.mediaName,
	mediaId: configurationReducer.currentMediaId,
	playlistId: configurationReducer.playlistId,
	viewerEngagementModuleActivated: configurationReducer.viewerEngagementModuleActivated,
	isChatActivated: configurationReducer.isChatActivated,
	accountId: configurationReducer.accountId,
	duration: playerReducer.duration,
	isMediaLive: configurationReducer.isMediaLive,
	reactionUrl: configurationReducer.reactionUrl,
	currentPosition: playerReducer.currentPosition,
	showChatWindow: playerReducer.showChatWindow,
	polls: playerReducer.polls,
	qnas: playerReducer.qnas,
	questions: playerReducer.questions,
	playerReady: playerReducer.playerReady,
	showShoppingCart: playerReducer.showShoppingCart,
	isPollExpanded: configurationReducer.isPollExpanded,
	reactionId: playerReducer.reactionId,
	mediasData: configurationReducer.mediasData,
	showCountdownModule: configurationReducer.showCountdownModule,
	isPlaying: playerReducer.isPlaying,
	isCloseIncomingMessage: configurationReducer.isCloseIncomingMessage,
});

const mapDispatchToProps = (dispatch) => ({
	setShowLoginForm: (item) => dispatch(setShowLoginForm(item)),
	setShowBackgroundElements: (value) => dispatch(setShowBackgroundElements(value)),
	setReactionUrl: (value) => dispatch(setReactionUrl(value)),
	setReactionId: (value) => dispatch(setReactionId(value)),
	setPollExpanded: (value) => dispatch(setPollExpanded(value)),
	setChatUsername: (item) => dispatch(setChatUsername(item)),
	setChatEmail: (item) => dispatch(setChatEmail(item)),
	setChatUserId: (item) => dispatch(setChatUserId(item)),
	setViewerEngagementModuleActivated: (value) => dispatch(setViewerEngagementModuleActivated(value)),
	setChatActivated: (value) => dispatch(setChatActivated(value)),
	setPollsData: (polls) => dispatch(setPollsData(polls)),
	setQnAData: (qnas) => dispatch(setQnAData(qnas)),
	setQuestionsData: (questions) => dispatch(setQuestionsData(questions)),
	setMediasData: (medias) => dispatch(setMediasData(medias)),
	setCloseIncomingMessage: (value) => dispatch(setCloseIncomingMessage(value)),
});

export default compose(connect(mapStateToProps, mapDispatchToProps), withTranslation())(ChatOverlay);
